import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { AxiosError } from 'axios';
import ErrorHandler from '@/support/ErrorHandler';
import { SetItem } from '@/models/SetItem';
import { Set } from '@/models/Set';
import { currency } from '@/support/String';
import LoaderCard from '@/components/loader-card/LoaderCard';
import { Options } from '@/components/mi-dialog/MiDialog';
import { Order } from '@/models/Order';
import { OrderItem } from '@/models/OrderItem';
import _ from 'lodash';
import ImageSetGalleryDialog from '@/components/ImageSetGalleryDialog/ImageSetGalleryDialog.vue';

@Component<OrderRelatedSets>({
  components: {
    LoaderCard,
    ImageSetGalleryDialog,
  },
  filters : {
    currency(value: string) {
      return !value ? '' : currency(parseFloat(value).toFixed(2) as any);
    },
  }
})

export default class OrderRelatedSets extends Vue {
  @Prop()
  protected order!: Order;

  protected sets: SetItem[] = [];
  protected setsClone: SetItem[] = [];
  protected explanation: ExplanationMap = {};

  protected isLoading: boolean = false;
  protected isSetItemsEmpty: boolean = false;

  protected selectedSetItem: SetItem | null = null;
  protected selectedSet: SetItem | null = null;

  protected isShowingImageSetGalleryDialog: boolean = false;
  protected dialogCurrentSetId: string = '';

  protected mounted() {
    this.initialize();
  }

  protected initialize() {
    this.fetchSets();
  }

  protected fetchSets() {
    this.isLoading = true;

    new SetItem()
    .include(['set-items', 'product', 'color', 'supplier', 'size', 'brand', 'season', 'images'])
    .order(this.$route.params.id)
    .all()
    .then((sets: SetItem[]) => {
      sets.forEach((set: SetItem) => {
        set.explanation = false;
      })
      this.sets = sets;
      this.checkSetItemsLength(sets);
      this.createEditMap(sets);
      this.isLoading = false;
    })
    .catch((error: AxiosError) => {
      ErrorHandler.network(error);
    });
  }

  protected createEditMap(sets: SetItem[]) {
    const explanationMap: ExplanationMap = {};
    sets.map((set: SetItem) => {
      if (!set || !set.id) {
        return;
      }

      explanationMap[set.id] = {
        id: set.id,
        editing: false,
      };
    });

    this.$set(this, 'explanation', explanationMap);
  }

  protected checkSetItemsLength(sets: SetItem[]) {
    let setItemsLength: number = 0;
    sets.map((set: SetItem) => {
      if (!set || !set.set_items) {
        return;
      }

      return setItemsLength += set.set_items.length;
    });

    if (setItemsLength === 0) {
      return this.isSetItemsEmpty = true;
    }

    return this.isSetItemsEmpty = false;
  }

  protected removeSetItem(setItem: SetItem, set: SetItem) {
    this.selectedSetItem = setItem;
    this.selectedSet = set;
    this.$store.dispatch('openDialog', this.dialogOptionsDeleteSetItem);
  }

  protected editingExplanation(setItem: SetItem, editing: boolean) {
    this.explanation[setItem.id as any].editing = editing;

    if (!editing) {
      this.resetExplanation(setItem);
    }
  }

  protected resetExplanation(setItem: SetItem) {
    this.sets.find((set: SetItem) => {
      if (set.id === setItem.id) {
        setItem.text = set.text;
      }
    })
  }

  protected updateExplanation(set: SetItem) {
    const setId: any = set.id;
    const payload = {
      text: set.text,
    }

    new Set()
    .updateOrderSet(this.$route.params.id, set.id as string, payload)
    .then((set: SetItem) => {
      if (!set) {
        return;
      }
      this.explanation[setId].editing = false;
      this.initialize();
    })
    .catch((error: AxiosError) => {
      ErrorHandler.network(error);
    })
  }

  protected onClickOpenImageSetGalleryDialog(setItems: SetItem): void {
    this.dialogCurrentSetId = setItems.id || '';
    this.isShowingImageSetGalleryDialog = true;
  }

  protected onCloseDialog(): void {
    this.isShowingImageSetGalleryDialog = false;
    this.dialogCurrentSetId = '';
  }

  get dialogOptionsDeleteSetItem(): Options {
    return {
      title: this.$t('dialogOptions.confirmation').toString(),
      text: 'Weet je zeker dat je deze set item wilt verwijderen?',
      type: 'warning',
      buttons: {
        confirm: {
          text: this.$t('dialogOptions.button.delete').toString(),
          action: () => {
            if (!this.selectedSetItem || !this.order || !this.selectedSet) {
              return;
            }

            new OrderItem()
            .deleteOrderItem((this.order.id as string), ((this.selectedSetItem as SetItem).order_item_id as string))
            .then(() => {
              this.fetchSets();
              this.$emit('orderItemDeleted');
            })
            .catch((error: AxiosError) => {
              ErrorHandler.network(error);
            });
          },
        },
        cancel: {
          text: this.$t('dialogOptions.button.cancel').toString(),
          action: () => {
            this.selectedSetItem = null;
          },
        },
      },
    };
  }

  @Watch('sets')
  protected setsChanged() {
    this.setsClone = _.cloneDeep(this.sets)
  }
}


interface ExplanationMap {
  [key: string]: Explanation;
}

interface Explanation {
  id: string;
  editing: boolean;
}
